import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Activity, AlertTriangle, Globe, Search, Clock, Wifi } from 'lucide-react';
import { API_URL } from '@/services/apiClient';

interface FeedItem {
  id: string;
  kind: 'alert' | 'event';
  studentName: string;
  title: string;
  // alert fields
  severity?: string;
  type?: string;
  status?: string;
  // event fields
  eventType?: string;
  domain?: string;
  isFlagged?: boolean;
  createdAt: string;
}

interface LiveActivityData {
  feed: FeedItem[];
  activeStudentCount: number;
}

function SiteFavicon({ domain, dm }: { domain: string | null | undefined; dm: boolean }) {
  const [failed, setFailed] = useState(false);
  if (!domain || failed) {
    return <Globe className={`h-4 w-4 shrink-0 ${dm ? 'text-white' : 'text-slate-400'}`} />;
  }
  const cleanDomain = domain.replace(/^https?:\/\//, '').split('/')[0];
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(cleanDomain)}&sz=32`}
      alt=""
      className="h-4 w-4 shrink-0 rounded-sm object-contain"
      onError={() => setFailed(true)}
    />
  );
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'text-red-500',
  high: 'text-orange-500',
  medium: 'text-amber-500',
  low: 'text-blue-400',
};


function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

export function LiveActivityFeed({ dm }: { dm: boolean }) {
  const [data, setData] = useState<LiveActivityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [filter, setFilter] = useState<'all' | 'alerts' | 'events' | 'flagged'>('all');

  const cardCls = `border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-white' : 'text-slate-800';
  const descCls = dm ? 'text-white' : 'text-slate-500';
  const bodyTextCls = dm ? 'text-white' : 'text-slate-700';
  const mutedCls = dm ? 'text-white' : 'text-slate-400';
  // const borderCls = dm ? 'border-slate-700' : 'border-slate-200';
  const dividerCls = dm ? 'divide-slate-700' : 'divide-slate-100';

  const fetch_ = useCallback(async () => {
    const token = sessionStorage.getItem('authToken');
    try {
      const res = await fetch(`${API_URL}/dashboard/school-admin/live-activity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setData(await res.json());
        setLastRefresh(new Date());
      }
    } catch {
      // silently fail — live feed is best-effort
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch_();
    const interval = setInterval(fetch_, 15_000);
    return () => clearInterval(interval);
  }, [fetch_]);

  const filtered = (data?.feed || []).filter(item => {
    if (filter === 'alerts') return item.kind === 'alert';
    if (filter === 'events') return item.kind === 'event';
    if (filter === 'flagged') return item.kind === 'alert' || item.isFlagged;
    return true;
  });

  const filterBtn = (key: typeof filter, label: string) => (
    <button
      onClick={() => setFilter(key)}
      className={`btn-glass btn-glass-pill text-xs ${filter === key ? 'btn-glass-blue' : ''}`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-5">
      {/* Header stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className={cardCls}>
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${dm ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                <Wifi className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className={`text-xs ${descCls}`}>Students Online Now</p>
                <p className={`text-2xl font-bold ${titleCls}`}>{data?.activeStudentCount ?? 0}</p>
                <p className={`text-xs ${mutedCls}`}>Last 10 minutes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cardCls}>
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${dm ? 'bg-red-900/50' : 'bg-red-100'}`}>
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className={`text-xs ${descCls}`}>Alerts (Last Hour)</p>
                <p className={`text-2xl font-bold ${titleCls}`}>
                  {data?.feed.filter(i => i.kind === 'alert').length ?? 0}
                </p>
                <p className={`text-xs text-red-500`}>
                  {data?.feed.filter(i => i.kind === 'alert' && (i.severity === 'critical' || i.severity === 'high')).length ?? 0} high/critical
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cardCls}>
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${dm ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                <Activity className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className={`text-xs ${descCls}`}>Web Events (Last Hour)</p>
                <p className={`text-2xl font-bold ${titleCls}`}>
                  {data?.feed.filter(i => i.kind === 'event').length ?? 0}
                </p>
                <p className={`text-xs text-amber-500`}>
                  {data?.feed.filter(i => i.kind === 'event' && i.isFlagged).length ?? 0} flagged
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feed */}
      <Card className={cardCls}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className={`text-base flex items-center gap-2 ${titleCls}`}>
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                Live Activity Stream
              </CardTitle>
              <CardDescription className={descCls}>
                Auto-refreshes every 15 seconds · Updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap" data-dm={dm}>
              {filterBtn('all', 'All')}
              {filterBtn('alerts', 'Alerts Only')}
              {filterBtn('events', 'Web Events')}
              {filterBtn('flagged', 'Flagged')}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className={`flex flex-col items-center justify-center h-40 ${mutedCls}`}>
              <Activity className="h-10 w-10 mb-2 opacity-40" />
              <p className="text-sm">No activity in the last hour</p>
            </div>
          ) : (
            <div className={`divide-y ${dividerCls}`}>
              {filtered.map(item => {
                const isAlert = item.kind === 'alert';
                const bg = dm ? 'bg-slate-700/40' : 'bg-slate-100';

                return (
                  <div key={`${item.kind}-${item.id}`} className="flex items-start gap-3 py-3">
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${bg}`}>
                      {isAlert ? (
                        <AlertTriangle className={`h-4 w-4 ${SEVERITY_COLORS[item.severity || 'low']}`} />
                      ) : item.eventType === 'search' ? (
                        <Search className={`h-4 w-4 ${item.isFlagged ? 'text-amber-500' : (dm ? 'text-white' : 'text-slate-500')}`} />
                      ) : (
                        <SiteFavicon domain={item.domain} dm={dm} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-medium ${bodyTextCls}`}>{item.studentName}</span>
                        {isAlert && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium capitalize ${
                            item.severity === 'critical' ? 'bg-red-100 text-red-700' :
                            item.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                            item.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {item.severity}
                          </span>
                        )}
                        {item.isFlagged && !isAlert && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">Flagged</span>
                        )}
                      </div>
                      <p className={`text-xs mt-0.5 truncate ${descCls}`}>
                        {isAlert ? `Alert: ${item.title}` : item.title}
                      </p>
                      {item.domain && (
                        <p className={`text-xs ${mutedCls}`}>{item.domain}</p>
                      )}
                    </div>
                    <div className={`flex items-center gap-1 text-xs shrink-0 ${mutedCls}`}>
                      <Clock className="h-3 w-3" />
                      {timeAgo(item.createdAt)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
